import React, { useId } from 'react';
import classNames from 'classnames';
import IconMediafileEmenu from '@zvuk-b2b/react-uikit/ui/icons/MediafileEmenu';
import IconVolume2 from '@zvuk-b2b/react-uikit/ui/icons/Volume2';
import IconVolumeX from '@zvuk-b2b/react-uikit/ui/icons/VolumeX';
import { MediafilePosterImage } from './MediafilePosterImage/MediafilePosterImage';
import { MediafilePosterPreviewTrigger } from './MediafilePosterPreviewTrigger/MediafilePosterPreviewTrigger';
import { DATA_TEST_IDS } from './consts';
import { TEXTS } from './texts';
import type { MediafilePosterProps } from './types';
import styles from './index.module.scss';

export const MediafilePoster: React.FC<MediafilePosterProps> = ({
  id,
  className,
  state,
  kind,
  thumbnail,
  fallbackTone,
  formatLabel,
  durationLabel,
  sound,
  preview,
}) => {
  const generatedId = useId();
  const instanceId = id ?? generatedId;
  const hasPreview = state === 'ready' && !!preview && !preview.isDisabled;
  return (
    <div
      className={classNames(styles.container, className, hasPreview && styles.previewAvailable)}
      data-testid={DATA_TEST_IDS.ROOT(instanceId)}>
      <div
        className={classNames(
          styles.plane,
          state === 'uploading' && styles.uploading,
          state === 'upload-error' && styles.uploadError,
          kind === 'emenu' && styles.emenu,
          state === 'ready' && kind !== 'emenu' && styles[`tone${fallbackTone}`]
        )}>
        {thumbnail?.src && (
          <MediafilePosterImage
            key={JSON.stringify([thumbnail.src, thumbnail.fallbackSrc])}
            id={instanceId}
            src={thumbnail.src}
            fallbackSrc={thumbnail.fallbackSrc}
            alt={thumbnail.alt}
          />
        )}
        {kind === 'emenu' && (
          <span
            aria-hidden="true"
            data-testid={DATA_TEST_IDS.EMENU(instanceId)}>
            <IconMediafileEmenu />
          </span>
        )}
      </div>
      <div className={styles.badges}>
        {sound && (
          <span
            role="img"
            aria-label={sound.label}
            className={styles.sound}
            data-testid={DATA_TEST_IDS.SOUND(instanceId)}>
            {sound.state === 'enabled' ? (
              <IconVolume2 className={styles.soundIcon} />
            ) : (
              <IconVolumeX className={styles.soundIcon} />
            )}
          </span>
        )}
        {(formatLabel || kind === 'emenu') && (
          <span
            className={styles.format}
            data-testid={DATA_TEST_IDS.FORMAT(instanceId)}>
            {kind === 'emenu' ? TEXTS.EMENU : formatLabel}
          </span>
        )}
        {durationLabel && (
          <span
            className={styles.duration}
            data-testid={DATA_TEST_IDS.DURATION(instanceId)}>
            {durationLabel}
          </span>
        )}
      </div>
      {preview && (
        <MediafilePosterPreviewTrigger
          id={instanceId}
          label={preview.label}
          onRequest={preview.onRequest}
          isDisabled={preview.isDisabled}
        />
      )}
    </div>
  );
};
